import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import useLineItemStatus from '../../hooks/useLineItemStatus';
import { formatDateTimeEt } from '../../util/helpers';

interface StatusDotRowProps {
  label: string;
  spent: boolean;
}

// One recency signal: a green dot when the line item spent inside the window, red when it did not.
// The timestamp is deliberately not per-row — see the note on the "Last spend" line below.
function StatusDotRow({ label, spent }: StatusDotRowProps) {
  // The dot carries the row's label as well as its state, so a screen reader reaching it out of
  // context announces "Spend in the last hour: No" rather than a bare, ambiguous "No". The visible
  // label is then aria-hidden: its text lives on in the dot's accessible name, and leaving it
  // exposed would make assistive tech announce the label twice per row.
  const accessibleName = `${label}: ${spent ? 'Yes' : 'No'}`;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary" aria-hidden>
        {label}:
      </Typography>
      <Box
        aria-label={accessibleName}
        role="img"
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: spent ? 'success.main' : 'error.main',
          flexShrink: 0,
        }}
      />
    </Stack>
  );
}

interface LineItemSpendStatusProps {
  lineItemId: number;
}

// The BigQuery-backed delivery recency signals for a line item (ADOPS-2733), shown at the top of
// the Daily Spend Activity modal — the OneDSP home for what the old Ventura campaign Status page
// reported. Line-item (IO) grain only: the endpoint's per-AdGroup breakdown reads a different
// BigQuery table and does not sum to these totals, so it is deliberately not rendered here.
// Renders nothing while loading, if the status endpoint fails, or if it answers with anything
// other than the expected shape, so the spend table below is never blocked by it.
function LineItemSpendStatus({ lineItemId }: LineItemSpendStatusProps) {
  const { data, isLoading } = useLineItemStatus(lineItemId);

  // `data?.io` rather than just `data`: getRequestJSON resolves the raw body text for any 2xx
  // that isn't JSON (an HTML login or maintenance page reached through a redirect, say), and a
  // truthy string would pass a bare `!data` check and then throw on the destructure below.
  if (isLoading || !data?.io) {
    return null;
  }

  const { spent_last_hour: spentLastHour, spent_last_24_hours: spentLast24Hours, last_spent_at: lastSpentAt } = data.io;

  return (
    <Stack spacing={0.5} mt={1} data-testid="line-item-spend-status">
      <StatusDotRow label="Spend in the last hour" spent={spentLastHour} />
      <StatusDotRow label="Spend in the last 24 hours" spent={spentLast24Hours} />
      {/* One timestamp for the whole panel, not one per row: last_spent_at is a single fact about
          the line item, and any spend inside the last hour is necessarily inside the last 24 hours,
          so a per-row timestamp would print the identical value twice for every actively
          delivering line item. Omitted entirely when there is no spend in the window the endpoint
          covers (last_spent_at null), which is also when both dots are red. */}
      {lastSpentAt && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Last spend:
          </Typography>
          <Typography variant="body2">{formatDateTimeEt(lastSpentAt)}</Typography>
        </Stack>
      )}
    </Stack>
  );
}

export default LineItemSpendStatus;
